/**
 * Store pour la liste des thématiques IA de l'utilisateur connecté
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

import { useManagingUser } from '@compositions/ManagingUser.js';

export const useAuthAIThematicStore = defineStore('authAIThematic', (() => {

    const {
        getManagingUser,
        updateManagingUser
    } = useManagingUser({});

    // Liste des thématiques par identifiant d'utilisateur
    const thematicsByUserId = ref({});

    // Retourne si la liste des thématiques a été chargé pour l'utilisateur
    const loadedByUserId = computed(() => {
        return ((userId) => {
            return ((thematicsByUserId.value[userId] ?? null) !== null);
        });
    });

    // Retourne la liste des thématiques par identifiant d'utilisateur
    const getListByUserId = computed(() => {
        return ((userId) => {
            return (thematicsByUserId.value[userId] ?? []);
        });
    });

    // Retourne la liste des thématiques du réseau en paramètre pour l'utilisateur en paramètre
    const getListOfUserAndNetwork = computed(() => {
        return ((userId, network) => {
            const thematicsByNetwork = getListByUserId.value(userId);
            return thematicsByNetwork.filter((thematic) => thematic.network === network);
        });
    });

    // Retourne la thématique dont l'identifiant est en paramètre
    const getThematicById = computed(() => {
        return ((userId, id) => {
            const item = getListByUserId.value(userId).filter((thematic) => thematic.id === id).at(0);
            return (item ?? null);
        });
    });

    /**
     * Modifie les thématiques du store par celles en paramètre
     * @param array newThematics
     * @return void
     */
    const setUserThematics = ((userId, newThematics) => {
        thematicsByUserId.value[userId] = newThematics;
    });

    /**
     * Chargement de la liste des thématiques
     * @param int userId
     * @param boolean force Vrai si on force le chargement
     * @return Promise
     */
    const loadUserThematics = ((userId, force = false) => {

        if(! force && loadedByUserId.value(userId)) {
            return Promise.resolve();
        }

        const path = `/api/users/${ userId }/ai-thematics`;

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.aiThematics) {
                setUserThematics(userId, response.data.aiThematics);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Vérifie la liste des thématiques (initialise la liste si elle ne l'a pas été)
     * @param int userId
     * @return Promise
     */
    const checkUserThematics = ((userId) => {
        const path = `/api/users/${ userId }/ai-thematics/check`;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.aiThematics) {
                setUserThematics(userId, response.data.aiThematics);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Ajout d'une thématique
     * @param int userId
     * @param Object postRequest
     * @return Promise
     */
    const addUserThematic = ((userId, postParams) => {
        const path = `/api/users/${ userId }/ai-thematics`;

        return axios.post(path, postParams).then((response) => {
            if(response && response.data && response.data.success && response.data.aiThematic) {
                let newThematic = null;
                if(loadedByUserId.value(userId)) {
                    thematicsByUserId.value[userId] ??= [];
                    thematicsByUserId.value[userId].push(response.data.aiThematic);
                    newThematic = getThematicById.value(userId, response.data.aiThematic.id);
                }
                return Promise.resolve(newThematic);
            }
            return Promise.reject();
        }).catch((response) => {
            return Promise.reject(response);
        });
    });

    /**
     * Régénération d'une thématique (et de ses sujets)
     * @param int userId
     * @param int thematicId
     * @return Promise
     */
    const regenerateThematic = ((userId, thematicId) => {
        const path = `/api/users/${ userId }/ai-thematics/${ thematicId }/regenerate`;

        return axios.post(path).then((response) => {

            const success = (response?.data?.success ?? false);
            const newAiThematic = (response?.data?.aiThematic ?? null);
            const newRemainingGenerations = (response?.data?.remainingThematicGenerations ?? null);

            if(! success) {
                return Promise.reject();
            }

            // Mise à jour du nombre de générations restantes
            const currentManagingUser = getManagingUser.value;
            if(currentManagingUser.id === userId && newRemainingGenerations !== null) {
                const currentManagingUser = Object.assign({}, JSON.parse(JSON.stringify(getManagingUser.value)));
                currentManagingUser.settings.maxRemainingThematicsGenerations = newRemainingGenerations;

                // Utilisation d'un timer le temps, d'exécution des événements des composants
                setTimeout(() => {
                    updateManagingUser(currentManagingUser);
                }, 200);
            }

            if(newAiThematic !== null) {
                if(loadedByUserId.value(userId)) {
                    const indexToUpdate = thematicsByUserId.value[userId].findIndex((currentThematic) => currentThematic.id === thematicId);
                    if(indexToUpdate !== -1) {
                        thematicsByUserId.value[userId][indexToUpdate] = newAiThematic;
                    }
                }
                return Promise.resolve(newAiThematic);
            }
            return Promise.reject();
        }).catch((response) => {
            return Promise.reject(response);
        });
    });

    /**
     * Mise à jour de la thématique en paramètre
     * @param int userId
     * @param int thematicId
     * @param Object postParams
     * @return Promise
     */
    const updateUserThematic = ((userId, thematicId, postParams) => {

        const path = `/api/users/${ userId }/ai-thematics/${ thematicId }`;

        return axios.post(path, postParams).then((response) => {
            if(response && response.data && response.data.success && response.data.aiThematic) {
                if(loadedByUserId.value(userId)) {
                    const indexToUpdate = thematicsByUserId.value[userId].findIndex((currentThematic) => currentThematic.id === thematicId);
                    if(indexToUpdate !== -1) {
                        thematicsByUserId.value[userId][indexToUpdate] = response.data.aiThematic;
                    }
                }
                return Promise.resolve();
            }
            return Promise.reject();
        }).catch((response) => {
            return Promise.reject(response);
        });

    });

    /**
     * Génère des propositions de sujets pour la thématique en paramètre
     * @param int userId
     * @param Object thematic
     * @param string|null customerPrompt Prompt facultatif du client
     * @param int nbSubjects Nombre de sujets à générer
     * @return Promise
     */
    const generateUserThematicSubjects = ((userId, thematic, customerPrompt, nbSubjects) => {
        const path = `/api/users/${ userId }/ai-thematics/${ thematic.id }/subjects/generate`;
        return axios.post(path, {
            customerPrompt: customerPrompt,
            nbSubjects: nbSubjects
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.subjects) {
                return Promise.resolve(response.data.subjects);
            }
            return Promise.reject();
        });
    });

    /**
     * Suppression de la thématique en paramètre
     * int userId
     * @param Object thematic
     * @return Promise
     */
    const removeUserThematic = ((userId, thematic) => {

        const path = `/api/users/${ userId }/ai-thematics/${ thematic.id }`;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                if(loadedByUserId.value(userId)) {
                    const indexToDelete = thematicsByUserId.value[userId].findIndex((currentThematic) => currentThematic.id === thematic.id);
                    if(indexToDelete !== -1) {
                        thematicsByUserId.value[userId].splice(indexToDelete, 1);
                    }
                }
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    return {
        loadedByUserId,
        getListByUserId,
        getListOfUserAndNetwork,
        getThematicById,
        setUserThematics,
        loadUserThematics,
        checkUserThematics,
        addUserThematic,
        updateUserThematic,
        removeUserThematic,
        generateUserThematicSubjects,
        regenerateThematic
    };

}));


